// Resultado do mês por vendedor — a faixa "Resultado de <mês>" da barra Meu Trabalho
// do CRM (pedido da Talita Bastos, out/2026). Funções puras, testadas em
// __tests__/resultadoMes.test.ts; a busca no banco fica em useResultadoMes.ts.
//
// - Etapas (#6426): jornada que se soma — ver ResultadoResponsavel. Envio e assinatura
//   pela data do evento, igual ao funil do Analytics (eventFunnel.ts); validação,
//   reprovados e aprovados pelo status de hoje dos assinados no mês.
// - Base da comissão: aprovação dentro do mês (aprovacaoAindaValida) — decisão aprovada
//   da análise cadastral e negociação não perdida/reprovada depois.
// - Comissão garantida: a conta da Folha de Pagamento sobre as aprovadas do mês,
//   como se o mês fechasse hoje. Fica de fora o que só se decide no fechamento:
//   salário, bônus de SLA do Comercial Interno, rede/safra/clawback do externo e a
//   2ª parcela do parceiro (que espera a 1ª fatura do cliente).
//
// Diferenças conhecidas em relação à Folha, todas para o lado conservador: a Folha
// conta o mês pelo relógio UTC e paga a aprovação mesmo se a negociação for perdida
// depois (o externo devolve no clawback); aqui o mês é o do fuso local e a perdida sai.
import { defDaEtapa } from '../Analytics/eventFunnel.ts';
import type { EventFunnelRow } from '../Analytics/eventFunnel.ts';
import { aprovacaoAindaValida } from '../../../src/lib/fechamentoMensal.ts';
import { calcularComissoesInterno, isCompetenciaNovaPoliticaInterno } from '../../../src/lib/comissaoInterna.ts';
import { calcularAtivacaoV2, isCompetenciaNovaPolitica, SLA_PENALIDADE_FATOR } from '../../../src/lib/politicaComercial.ts';
import { calcularComissaoParceiro, isGrupoTarifarioA } from '../../../src/lib/comissaoUtils.ts';
import { calcularBonificacaoDasVendas, MOTIVO_SEM_DESCONTO } from '../../../src/lib/bonificacaoParceiroFolha.ts';
import type { BonificacaoDaVenda } from '../../../src/lib/bonificacaoParceiroFolha.ts';
import { ehComercialInterno } from '../../../src/lib/departamentos.ts';
import { situacaoNaValidacao } from '../../../src/lib/validacaoDados.ts';
import type { Profile } from '../../../types';

// ── Mês ────────────────────────────────────────────────────────────────────

export interface JanelaMes {
    mes: number; // 1-12
    ano: number;
    inicio: Date;
    /** 1º dia do mês seguinte, 00:00 — exclusivo. */
    fimExclusivo: Date;
}

/** Mês-calendário no fuso do navegador, o mesmo do "Mês atual" do Analytics. */
export function janelaDoMes(ref: Date = new Date()): JanelaMes {
    const inicio = new Date(ref.getFullYear(), ref.getMonth(), 1);
    const fimExclusivo = new Date(ref.getFullYear(), ref.getMonth() + 1, 1);
    return { mes: inicio.getMonth() + 1, ano: inicio.getFullYear(), inicio, fimExclusivo };
}

export function dentroDoMes(iso: string | null | undefined, janela: JanelaMes): boolean {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return !Number.isNaN(t) && t >= janela.inicio.getTime() && t < janela.fimExclusivo.getTime();
}

// ── Linha da negociação ────────────────────────────────────────────────────

export interface LinhaResultadoMes {
    id: number;
    responsavel_id: string | null;
    status: string | null;
    consumo_medio_kwh: number | null;
    created_at?: string | null;
    data_assinatura_termo?: string | null;
    url_termo?: Array<{ data_criacao?: string | null }> | null;
    analise_cadastral?: Array<{ decisao_final?: string | null; decisao_final_at?: string | null }>;
    // Campos do plano — só vêm na consulta das aprovadas (comissão)
    fidelidade?: string | null;
    origem?: string | null;
    plano_codigo?: string | null;
    publico?: string | null;
    multiplicador_final?: number | null;
    unidades_consumidoras?: Array<{
        numero_uc: string;
        grupo_tarifario?: string | null;
        desconto_tarifa?: number | null;
        desconto_bandeira?: number | null;
    }> | null;
    /** Venda indicada pelo pós-venda (#6236): comissão 120%, metade para quem vendeu. */
    pos_venda_id?: string | null;
    /** Bonificação do parceiro: o mesmo CPF/CNPJ só conta uma vez no mês. */
    cpf_cnpj?: string | null;
}

/**
 * Junta as duas consultas (termos do mês + aprovadas do mês) numa linha por
 * negociação. A mesma negociação pode vir nas duas, cada uma com um pedaço dos
 * campos — o que uma traz não pode ser apagado pela outra.
 */
export function mesclarLinhas(...listas: LinhaResultadoMes[][]): LinhaResultadoMes[] {
    const porId = new Map<number, LinhaResultadoMes>();
    for (const lista of listas) {
        for (const l of lista) {
            const atual = porId.get(Number(l.id));
            if (!atual) { porId.set(Number(l.id), { ...l }); continue; }
            const mesclada: any = { ...atual };
            for (const [k, v] of Object.entries(l)) if (v !== undefined) mesclada[k] = v;
            porId.set(Number(l.id), mesclada);
        }
    }
    return [...porId.values()];
}

// Mesmas datas do funil do Analytics — se a regra de lá mudar, muda aqui junto.
const dataEnvioTermo = defDaEtapa('termo_enviado')!.dataDe;
const dataAssinatura = defDaEtapa('termo_assinado')!.dataDe;

const comoLinhaFunil = (l: LinhaResultadoMes): EventFunnelRow => ({
    ...(l as any),
    status: l.status || '',
    created_at: l.created_at || '',
});

/** A negociação conta como aprovada neste mês? (mesma regra do funil do Analytics) */
export function aprovadaNoMes(l: LinhaResultadoMes, janela: JanelaMes): boolean {
    return dentroDoMes(aprovacaoAindaValida(l as any), janela);
}

// ── Apuração por responsável ──────────────────────────────────────────────

export interface Contagem {
    qtd: number;
    kwh: number;
}

/**
 * Jornada do mês (#6426, pedido da Talita): etapas que se SOMAM, para a diferença de uma
 * para a outra fechar certinho. Antes cada número contava pela data do próprio evento
 * (aprovado em outubro de termo assinado em setembro entrava em "aprovados" e não em
 * "assinados") e as etapas não batiam entre si.
 *
 * - Em assinatura: termo enviado no mês que ainda espera o cliente assinar.
 * - Assinados: termo assinado no mês. As etapas seguintes repartem ESTA turma pelo
 *   status de hoje, então assinados = validação + pendência + reprovados + aprovados
 *   (+ saídos). Validação = na fila da analista; pendência = já analisado, esperando o
 *   cliente (regra em src/lib/validacaoDados.ts, 03/11/2026).
 * - Aprovados: mesma conta do "Vendas Efetivas" do Dashboard (src/lib/dashboard/vendasEfetivas.ts).
 *
 * A comissão NÃO segue a turma: continua pela regra da Folha (aprovação dentro do mês),
 * em `aprovadas`. A diferença entre as duas é `aprovadasDeOutroMes`.
 */
export interface ResultadoResponsavel {
    responsavelId: string;
    /** Termo enviado no mês e ainda esperando assinatura (status termo_enviado). */
    emAssinatura: Contagem;
    /** Termo assinado no mês — a turma que as etapas seguintes repartem. */
    assinados: Contagem;
    /** Dos assinados no mês: na fila da validação de dados, esperando a analista. */
    validacao: Contagem;
    /** Dos assinados no mês: já analisados e com pendência, esperando o cliente (fora da fila). */
    pendencia: Contagem;
    /** Dos assinados no mês: reprovados. */
    reprovados: Contagem;
    /** Dos assinados no mês: aprovados. */
    aprovados: Contagem;
    /** Dos assinados no mês: perdidos depois de assinar (ou outro status) — fecha a conta. */
    saidos: Contagem;
    /** Aprovadas no mês pela data da aprovação — base da comissão (regra da Folha). */
    aprovadas: LinhaResultadoMes[];
    /** Das aprovadas no mês, as de termo assinado antes do mês (estão na comissão, não em "aprovados"). */
    aprovadasDeOutroMes: number;
}

export type EtapaDoAssinado = 'validacao' | 'pendencia' | 'reprovados' | 'aprovados' | 'saidos';

/** Onde está hoje um termo assinado no mês. */
export function etapaDoAssinado(status: string | null | undefined): EtapaDoAssinado {
    const naValidacao = situacaoNaValidacao(status);
    if (naValidacao === 'fila') return 'validacao';
    if (naValidacao === 'pendencia') return 'pendencia';
    if (status === 'termo_reprovado') return 'reprovados';
    if (status === 'termo_aprovado') return 'aprovados';
    return 'saidos';
}

const vazio = (): Contagem => ({ qtd: 0, kwh: 0 });

export function resultadoVazio(responsavelId: string): ResultadoResponsavel {
    return {
        responsavelId,
        emAssinatura: vazio(), assinados: vazio(), validacao: vazio(), pendencia: vazio(), reprovados: vazio(), aprovados: vazio(), saidos: vazio(),
        aprovadas: [], aprovadasDeOutroMes: 0,
    };
}

const somar = (c: Contagem, kwh: number) => { c.qtd++; c.kwh += kwh; };

/** Monta a jornada do mês por responsável (ver ResultadoResponsavel). */
export function apurarResultadoMes(linhas: LinhaResultadoMes[], janela: JanelaMes): Map<string, ResultadoResponsavel> {
    const porResp = new Map<string, ResultadoResponsavel>();
    const vistos = new Set<number>();
    for (const l of linhas) {
        if (!l.responsavel_id || vistos.has(Number(l.id))) continue;
        vistos.add(Number(l.id));
        const kwh = Number(l.consumo_medio_kwh) || 0;
        const linhaFunil = comoLinhaFunil(l);
        const enviou = dentroDoMes(dataEnvioTermo(linhaFunil), janela);
        const assinou = dentroDoMes(dataAssinatura(linhaFunil), janela);
        const emAssinatura = enviou && !l.data_assinatura_termo && l.status === 'termo_enviado';
        const aprovou = aprovadaNoMes(l, janela);
        if (!emAssinatura && !assinou && !aprovou) continue;

        let r = porResp.get(l.responsavel_id);
        if (!r) { r = resultadoVazio(l.responsavel_id); porResp.set(l.responsavel_id, r); }
        if (emAssinatura) somar(r.emAssinatura, kwh);
        if (assinou) {
            somar(r.assinados, kwh);
            somar(r[etapaDoAssinado(l.status)], kwh);
        }
        if (aprovou) {
            r.aprovadas.push(l);
            if (!assinou) r.aprovadasDeOutroMes++;
        }
    }
    return porResp;
}

// ── Comissão garantida ────────────────────────────────────────────────────

/** Qual motor da Folha paga essa pessoa — mesma ordem de decisão da Folha. */
export type PoliticaComissao = 'interno_v1' | 'externo_v2' | 'parceiro';

type PerfilPolitica = Pick<Profile, 'role' | 'departamento' | 'departamentos'>;

/**
 * Espelha a Folha de Pagamento: aba Parceiros paga role parceiro (o vendedor_parceiro
 * gera comissão para o parceiro dono da rede, não para si); aba Vendedores paga role
 * vendedor e quem é do Comercial Interno (por departamento — a Júlia é `analista`).
 */
export function politicaDoPerfil(perfil: PerfilPolitica | null | undefined, mes: number, ano: number): PoliticaComissao | null {
    if (!perfil) return null;
    if (perfil.role === 'parceiro') return 'parceiro';
    if (perfil.role === 'vendedor_parceiro') return null;
    if (ehComercialInterno(perfil)) return isCompetenciaNovaPoliticaInterno(mes, ano) ? 'interno_v1' : null;
    if (perfil.role === 'vendedor') return isCompetenciaNovaPolitica(mes, ano) ? 'externo_v2' : null;
    return null;
}

export interface ParametrosComissao {
    /** R$/kWh vigente no mês; null = não foi possível ler. */
    tarifa: number | null;
    /** Meta do mês (herdada como na Folha); null = não foi possível ler. */
    metaKwh: number | null;
}

export interface ComissaoGarantida {
    politica: PoliticaComissao;
    /** R$ garantido com as aprovadas até agora. */
    valor: number;
    /** Aprovadas sem plano definido (desconto da UC) — não entram até definir. */
    semPlano: number;
    /** Aprovadas do Grupo A — cálculo manual do gerente, fora do automático. */
    grupoA: number;
    /** Parceiro: 2ª parcela (70%), só depois da 1ª fatura do cliente. */
    aposPrimeiraFatura: number;
    /** Externo: SLA do mês ainda não cumprido → ativação e threshold pela metade. */
    slaPendente: boolean;
    /** Aprovadas indicadas pelo pós-venda: 120% da comissão, metade fica com o pós-venda (#6236). */
    indicacoesPosVenda: number;
    /** Parceiro, regra de 26/10/2026: aprovadas que não entram na conta (abaixo de 500 kWh, desconto fora da regra...). */
    foraDaRegra?: number;
    /** Parceiro, regra de 26/10/2026: faixa do mês (somando a equipe). */
    faixaParceiro?: { faixa: string | null; pct: number; kwh: number } | null;
}

const ehGrupoA = (l: LinhaResultadoMes) => isGrupoTarifarioA(l.unidades_consumidoras?.[0]?.grupo_tarifario);

/**
 * Comissão que a pessoa já tem garantida com as aprovadas do mês. `null` quando
 * faltam os parâmetros que a conta exige (tarifa e/ou meta).
 */
export function calcularComissaoGarantida(
    politica: PoliticaComissao,
    aprovadas: LinhaResultadoMes[],
    params: ParametrosComissao,
    opcoes: {
        slaCumprido?: boolean;
        /**
         * Parceiro: bonificação da regra de 26/10/2026 já calculada com a turma do mês
         * inteiro (inclusive a equipe de vendedor_parceiro). Sem ela, a conta usa só
         * as aprovadas recebidas aqui.
         */
        bonificacao?: Map<number, BonificacaoDaVenda>;
    } = {},
): ComissaoGarantida | null {
    const base: ComissaoGarantida = { politica, valor: 0, semPlano: 0, grupoA: 0, aposPrimeiraFatura: 0, slaPendente: false, indicacoesPosVenda: 0 };
    const negs = aprovadas.map(l => ({ ...l, is_grupo_a: ehGrupoA(l) }));
    base.grupoA = negs.filter(n => n.is_grupo_a).length;
    // Só o Comercial Interno e o externo dividem com o pós-venda; parceiro segue a regra dele.
    if (politica !== 'parceiro') base.indicacoesPosVenda = negs.filter(n => !n.is_grupo_a && n.pos_venda_id).length;

    if (politica === 'interno_v1') {
        if (params.tarifa == null || params.metaKwh == null) return null;
        const r = calcularComissoesInterno(negs as any, params.metaKwh, params.tarifa);
        return { ...base, valor: Math.round(r.valorComissaoTotal * 100) / 100, semPlano: r.ignoradas.length };
    }

    if (politica === 'externo_v2') {
        if (params.metaKwh == null) return null;
        const r = calcularAtivacaoV2(negs as any, params.metaKwh);
        // SLA desconhecido conta como não cumprido: "garantido" não pode prometer o que depende do fim do mês.
        const slaPendente = negs.length > 0 && opcoes.slaCumprido !== true;
        const fator = slaPendente ? SLA_PENALIDADE_FATOR : 1;
        const valor = (r.valorAtivacao + r.valorThreshold) * fator;
        return { ...base, valor: Math.round(valor * 100) / 100, slaPendente };
    }

    // parceiro — aceite a partir de 26/10/2026: bonificação pela faixa do mês (garantida =
    // Pix I, depois da 1ª fatura = Pix II). Antes disso: regra antiga, conta inalterada.
    const bonificacao = opcoes.bonificacao ?? calcularBonificacaoDasVendas(negs
        .filter(n => n.responsavel_id)
        .map(n => ({
            negociacao_id: Number(n.id),
            dono_id: n.responsavel_id!,
            decisao_final_at: n.analise_cadastral?.[0]?.decisao_final_at || '',
            consumo_medio_kwh: n.consumo_medio_kwh,
            cpf_cnpj: n.cpf_cnpj,
            ucs: n.unidades_consumidoras,
        })));
    const doCalculo = negs.filter(n => !n.is_grupo_a);
    const antigas = doCalculo.filter(n => !bonificacao.has(Number(n.id)));
    if (params.tarifa == null && (antigas.length > 0 || doCalculo.length === 0)) return null;
    let p1 = 0;
    let p2 = 0;
    let semPlano = 0;
    let foraDaRegra = 0;
    let faixaParceiro: ComissaoGarantida['faixaParceiro'] = null;
    for (const n of doCalculo) {
        const b = bonificacao.get(Number(n.id));
        if (b) {
            faixaParceiro ??= { faixa: b.faixa_mes, pct: b.faixa_percentual, kwh: b.kwh_mes };
            if (b.motivo_fora === MOTIVO_SEM_DESCONTO) semPlano++;
            else if (b.motivo_fora) foraDaRegra++;
            p1 += b.pix1;
            p2 += b.pix2;
            continue;
        }
        const c = calcularComissaoParceiro(Number(n.consumo_medio_kwh) || 0, params.tarifa!);
        p1 += c.parcela1;
        p2 += c.parcela2;
    }
    return {
        ...base,
        valor: Math.round(p1 * 100) / 100,
        aposPrimeiraFatura: Math.round(p2 * 100) / 100,
        semPlano,
        foraDaRegra,
        faixaParceiro,
    };
}

// ── Meta herdada (caminho alternativo, sem a função do banco) ─────────────

export interface RegistroMeta {
    vendedor_id: string;
    mes: number;
    ano: number;
    meta_kwh: number | string | null;
    salario_base: number | string | null;
}

/**
 * Meta do mês como a Folha herda (VendedoresTab + getLastMetasVendedores): o registro
 * do próprio mês se tiver meta ou salário; senão o mais recente anterior que tenha.
 * Espelha a função do banco `parametros_comissao_mes` — mudar as duas juntas.
 */
export function metaVigente(registros: RegistroMeta[], vendedorId: string, mes: number, ano: number): number {
    const candidatos = registros
        .filter(r => r.vendedor_id === vendedorId)
        .filter(r => r.ano < ano || (r.ano === ano && r.mes <= mes))
        .filter(r => (Number(r.meta_kwh) || 0) > 0 || (Number(r.salario_base) || 0) > 0)
        .sort((a, b) => (b.ano - a.ano) || (b.mes - a.mes));
    return Number(candidatos[0]?.meta_kwh) || 0;
}

// ── Total ──────────────────────────────────────────────────────────────────

export function somarContagens(itens: Contagem[]): Contagem {
    return itens.reduce((acc, c) => ({ qtd: acc.qtd + c.qtd, kwh: acc.kwh + c.kwh }), vazio());
}

/**
 * Comissão somada do recorte. `null` quando ninguém do recorte tem comissão de
 * vendedor, ou quando alguém que tem não pôde ser calculado — um total parcial
 * pareceria certo e não é.
 */
export function somarComissoes(itens: Array<{ politica: PoliticaComissao | null; comissao: ComissaoGarantida | null }>): number | null {
    const comPolitica = itens.filter(i => i.politica);
    if (comPolitica.length === 0) return null;
    if (comPolitica.some(i => !i.comissao)) return null;
    return Math.round(comPolitica.reduce((s, i) => s + (i.comissao?.valor || 0), 0) * 100) / 100;
}
